import React from "react";
import { motion } from "framer-motion";

/**
 * The Solano mascot — a half-flame / half-water-drop character with big
 * friendly eyes, a thumb-up and sneakers. Central component used across
 * loading states, empty states, achievements, coach tips and feedback
 * messages so the app has a consistent digital companion.
 *
 * Always keeps the same visual identity (orange / petrol Solano-Essence
 * drop) — only the animation loop changes based on the semantic state.
 *
 * Props:
 *   size    number   pixel size (defaults to 128). Auto-picks the best
 *                    WebP resolution (128 / 256 / full) to minimise bytes.
 *   pose    string   one of the semantic animation states below.
 *   className   extra Tailwind classes (positioning, rounded, etc.).
 *   alt         image alt text (defaults to "Solano").
 *
 * Semantic states — use these across the app so the mascot's emotion
 * always matches the moment:
 *
 *   neutral        Baseline gentle bob & sway. Passive presence (empty
 *                  states, generic loading, splash screens).
 *   analyzing      Focused tilt + slower breathing motion. Used while
 *                  the AI is working (photo analysis, coach thinking).
 *   happy          Warm smile-up motion — small scale-up + tiny rotate
 *                  wobble. Positive feedback, success confirmations,
 *                  streak-still-alive, healthy meal logged.
 *   congratulating Big celebration: bounce + rotate + scale burst.
 *                  Level-ups, achievement unlocks, challenge complete.
 *   motivating     Confident thumbs-up shake + slight forward lean.
 *                  Streak nudges, reminders, "one more meal to unlock…".
 *   attentive      Alert lean-in — subtle forward tilt + tiny scale.
 *                  Coach tips, health-score explanations, learn cards.
 *   bounce         Quick vertical bounce — full-page loaders.
 *   wave           Friendly rotate — greetings (login, onboarding).
 *   cheer          Legacy alias of `happy`.
 *   thinking       Legacy alias of `analyzing`.
 *   static         No animation.
 */
export const MASCOT_POSES = [
  "neutral", "analyzing", "happy", "congratulating", "motivating", "attentive",
  "bounce", "wave", "cheer", "thinking", "idle", "static",
];

/**
 * Variants — thematic outfits kept as separate WebP assets so we don't
 * have to overlay props at runtime. Same character identity in each.
 *   default  base Solano drop (neutral use everywhere)
 *   chef     chef hat + apron + pan + wooden spoon (Recipes / cooking)
 *   fitness  black tank top + shorts + dumbbell (Workouts / training)
 */
export const MASCOT_VARIANTS = ["default", "chef", "fitness"];

const POSE_VARIANTS = {
  // Baseline gentle presence — same as legacy `idle`, kept as the default.
  neutral: {
    animate: { y: [0, -4, 0], rotate: [-1, 1, -1] },
    transition: { duration: 3.2, repeat: Infinity, ease: "easeInOut" },
  },
  // Focused analysing — slower, slightly bigger tilt so it reads as
  // "concentrating on something".
  analyzing: {
    animate: { rotate: [-4, 4, -4], y: [0, -3, 0], scale: [1, 1.02, 1] },
    transition: { duration: 2.6, repeat: Infinity, ease: "easeInOut" },
  },
  // Warm positive feedback — small scale up + tiny sway.
  happy: {
    animate: { scale: [1, 1.06, 1], rotate: [-2, 2, -2] },
    transition: { duration: 1.4, repeat: Infinity, ease: "easeInOut" },
  },
  // Big celebration for level-ups & achievements — leaping bounce with a
  // rotation flourish.
  congratulating: {
    animate: { y: [0, -18, 0, -8, 0], rotate: [0, -8, 8, -4, 0], scale: [1, 1.12, 1] },
    transition: { duration: 1.4, repeat: Infinity, ease: "easeInOut" },
  },
  // Confident, encouraging — leans forward slightly and thumbs-up shakes.
  motivating: {
    animate: { rotate: [-3, 5, -3], y: [0, -6, 0], scale: [1, 1.04, 1] },
    transition: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
  },
  // Attentive — subtle forward lean-in, minimal motion so it doesn't
  // distract from the tip / explanation the user is reading.
  attentive: {
    animate: { rotate: [-2, 3, -2], y: [0, -1.5, 0] },
    transition: { duration: 4.5, repeat: Infinity, ease: "easeInOut" },
  },
  // Utility poses (kept from earlier work).
  idle: {
    animate: { y: [0, -4, 0], rotate: [-1, 1, -1] },
    transition: { duration: 3.2, repeat: Infinity, ease: "easeInOut" },
  },
  bounce: {
    animate: { y: [0, -14, 0] },
    transition: { duration: 0.9, repeat: Infinity, ease: "easeInOut" },
  },
  wave: {
    animate: { rotate: [-6, 8, -6] },
    transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
  },
  cheer: {
    animate: { scale: [1, 1.08, 1], rotate: [-3, 3, -3] },
    transition: { duration: 1.1, repeat: Infinity, ease: "easeInOut" },
  },
  thinking: {
    animate: { rotate: [-3, 3, -3], y: [0, -3, 0] },
    transition: { duration: 2.2, repeat: Infinity, ease: "easeInOut" },
  },
  static: { animate: {}, transition: {} },
};

export default function Mascot({ size = 128, pose = "neutral", variant = "default", className = "", alt = "Solano" }) {
  // Pick the folder-level prefix + extension based on variant. Chef and
  // fitness variants ship as transparent PNGs (explicit user request) so
  // the background never appears white against tinted headers. The
  // default drop stays as WebP because it's already alpha-clean and much
  // smaller.
  const base = variant === "chef" ? "chef" : variant === "fitness" ? "fitness" : "solano";
  const ext = variant === "default" ? "webp" : "png";
  const src = size <= 128 ? `/mascot/${base}-128.${ext}` : size <= 256 ? `/mascot/${base}-256.${ext}` : `/mascot/${base}.${ext}`;
  const dims = { width: size, height: size };
  const v = POSE_VARIANTS[pose] || POSE_VARIANTS.neutral;

  return (
    <motion.img
      src={src}
      alt={alt}
      draggable={false}
      loading="lazy"
      decoding="async"
      className={`select-none pointer-events-none ${className}`}
      style={{ ...dims }}
      animate={v.animate}
      transition={v.transition}
      data-testid="solano-mascot"
      data-pose={pose}
      data-variant={variant}
    />
  );
}
